import { plural } from '../../utils/plural';
import { portalPath } from '../../utils/portalPath';
import { formatDate } from '../../utils/formatDate';
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Search, Building2, Package, Users, Clock, Loader } from 'lucide-react';
import { clientsService, facilitiesService, assetsService, inspectionsService } from '../../services';
import './GlobalSearch.css';

interface SearchResult {
  id: string;
  type: 'client' | 'facility' | 'asset' | 'inspection';
  title: string;
  subtitle: string;
  context: string;
  path: string;
}

interface GlobalSearchProps {
  portalMode?: boolean;
  placeholder?: string;
  onResultClick?: (result: SearchResult) => void;
}

const GlobalSearch: React.FC<GlobalSearchProps> = ({
  portalMode = false,
  placeholder = "Search clients, facilities, assets...",
  onResultClick
}) => {
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [results, setResults] = useState<SearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  const searchAllEntities = useCallback(async (searchQuery: string): Promise<SearchResult[]> => {
    if (!searchQuery.trim() || searchQuery.trim().length < 2) return [];

    const searchText = searchQuery.toLowerCase().trim();
    const allResults: SearchResult[] = [];

    try {
      // Search all entities in parallel
      const [clients, facilities, assets, inspections] = await Promise.all([
        clientsService.getClients().catch(() => []),
        facilitiesService.getFacilities().catch(() => []),
        assetsService.getAssets().catch(() => []),
        inspectionsService.getInspections().catch(() => [])
      ]);

      // Filter and map clients
      clients
        .filter(client => client.name.toLowerCase().includes(searchText))
        .forEach(client => {
          allResults.push({
            id: client.id,
            type: 'client',
            title: client.name,
            subtitle: 'Client organization',
            context: 'Client',
            path: `/clients/${client.id}`
          });
        });

      // Filter and map facilities
      facilities
        .filter(facility =>
          facility.name.toLowerCase().includes(searchText) ||
          facility.address?.toLowerCase().includes(searchText)
        )
        .forEach(facility => {
          allResults.push({
            id: facility.id,
            type: 'facility',
            title: facility.name,
            subtitle: facility.address || 'No address',
            context: 'Facility',
            path: `/facilities/${facility.id}`
          });
        });

      // Filter and map assets
      assets
        .filter(asset =>
          asset.name.toLowerCase().includes(searchText) ||
          asset.type?.toLowerCase().includes(searchText) ||
          asset.description?.toLowerCase().includes(searchText) ||
          asset.location_path?.toLowerCase().includes(searchText) ||
          asset.location?.toLowerCase().includes(searchText)
        )
        .forEach(asset => {
          allResults.push({
            id: asset.id,
            type: 'asset',
            title: asset.name,
            subtitle: asset.type_name || asset.type.replace(/_/g, ' ') || 'Asset',
            context: asset.location_path || 'No location',
            path: `/facilities/${asset.facility_id}?asset=${asset.id}`
          });
        });

      // Filter and map inspections
      inspections
        .filter(inspection =>
          inspection.type?.toLowerCase().includes(searchText) ||
          inspection.status?.toLowerCase().includes(searchText)
        )
        .forEach(inspection => {
          allResults.push({
            id: inspection.id,
            type: 'inspection',
            title: `${inspection.type} Inspection`,
            subtitle: `Status: ${inspection.status}`,
            context: formatDate(inspection.created_at),
            path: `/inspections/${inspection.id}`
          });
        });

    } catch (error) {
      console.error('Search failed:', error);
    }

    // Limit to top 10 results
    return (portalMode ? allResults.filter(result => result.type !== 'client' && result.type !== 'asset').map(result => ({...result, path: portalPath(result.path)})) : allResults).slice(0, 10);
  }, [portalMode]);

  const getResultIcon = (type: string) => {
    switch (type) {
      case 'client': return Users;
      case 'facility': return Building2;
      case 'asset': return Package;
      case 'inspection': return Clock;
      default: return Search;
    }
  };

  const getResultColor = (type: string) => {
    switch (type) {
      case 'client': return '#0077be';
      case 'facility': return '#4fc3f7';
      case 'asset': return '#26c6da';
      case 'inspection': return '#29b6f6';
      default: return '#607d8b';
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Debounced search effect
  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    if (!query.trim() || query.trim().length < 2) {
      setResults([]);
      setIsOpen(false);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    debounceRef.current = setTimeout(async () => {
      const searchResults = await searchAllEntities(query);
      setResults(searchResults);
      setIsOpen(searchResults.length > 0);
      setIsLoading(false);
    }, 300); // 300ms debounce

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [query, searchAllEntities]);

  const handleResultClick = (result: SearchResult) => {
    setQuery('');
    setIsOpen(false);
    onResultClick?.(result);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
      inputRef.current?.blur();
    }
  };

  return (
    <div className="global-search" ref={searchRef}>
      <div className="search-input-container">
        <Search className="search-icon" size={18} />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => query.length >= 2 && results.length > 0 && setIsOpen(true)}
          onKeyDown={handleKeyDown}
          aria-label={portalMode ? 'Search your facilities' : 'Search clients, facilities and assets'}
          placeholder={placeholder}
          className="search-input"
        />
        {isLoading && (
          <Loader className="search-loading" size={16} />
        )}
        {query && !isLoading && (
          <button
            className="clear-search"
            onClick={() => {
              setQuery('');
              setIsOpen(false);
            }}
          >
            ×
          </button>
        )}
      </div>

      {isOpen && (
        <div className="search-results">
          <div className="search-results-header">
            <span className="results-count">{plural(results.length, 'result')}</span>
            <div className="search-tips">
              {portalMode ? 'Search your facilities' : 'Search across clients, facilities, assets, and inspections'}
            </div>
          </div>

          <div className="results-list">
            {results.map((result) => {
              const Icon = getResultIcon(result.type);
              const color = getResultColor(result.type);

              return (
                <div
                  key={`${result.type}-${result.id}`}
                  className="search-result"
                  onClick={() => handleResultClick(result)}
                >
                  <div className="result-icon" style={{ color }}>
                    <Icon size={16} />
                  </div>
                  <div className="result-content">
                    <div className="result-title">{result.title}</div>
                    <div className="result-subtitle">{result.subtitle}</div>
                  </div>
                  <div className="result-context">{result.context}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default GlobalSearch;
